import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { catchError, filter, finalize, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { InteractionRequiredAuthError, InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { environment } from 'apps/web/src/environments/environment';
import { JwtStateService } from '@net3-multiplatform-client/libs';


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private jwtStateService = inject(JwtStateService);

  private options = environment;
  private apiUrl;
  
  private readonly jwt = this.jwtStateService.jwt;

  private exchangeInFlight?: Observable<string>; // evita exchanges duplicats

  constructor(
    private authMsalService: MsalService,
    private http: HttpClient,
    private msalBroadcast: MsalBroadcastService,
    private msalService: MsalService,
  ) {
    this.apiUrl = `${this.options.baseUrl.auth}`;
    effect(() => {
      if(this.jwt()){
        localStorage.setItem('token', this.jwt()!);
      }
    })
  }

  /** Decodifica de forma segura y comprova l'expiració */
  isExpired(jwt: string | null): boolean {
    if (!jwt) return true;
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      const expMs = payload?.exp ? payload.exp * 1000 : 0;
      return Date.now() >= expMs;
    } catch {
      return true;
    }
  }

  /** Crida al backend per intercambiar el token MSAL per un JWT propi */
  private exchangeToken(): Observable<string> {
    let token = localStorage.getItem('token');
    if (token && !this.isExpired(token)){
      this.jwtStateService.setJwt(token)
      return of(token);
    } 
    if (this.exchangeInFlight) return this.exchangeInFlight;

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      console.error('No active account found.');
      return of("");
    }

    const silentRequest: SilentRequest = {
      scopes: environment.apiConfig.scopes,
      account: activeAccount,
      forceRefresh: false
    };

    const url = `${this.apiUrl}/auth/exchange`;

    // acquireTokenSilent refresca el idToken via refresh_token si el cache està obsolet,
    // evitant enviar al backend un token amb un kid ja rotat al JWKS d'Entra ID.
    this.exchangeInFlight = this.msalService.acquireTokenSilent(silentRequest).pipe(
      map(result => result.idToken),
      switchMap(idToken => {
        if (!idToken) {
          return throwError(() => new Error('No ID token returned from acquireTokenSilent'));
        }
        return this.http.post<{ token: string, expiresIn: number }>(url, { idToken }).pipe(
          map(res => res.token)
        );
      }),
      tap(jwt => this.jwtStateService.setJwt(jwt)),
      catchError(err => {
        // Si el refresh token també ha caducat, MSAL no pot refrescar en silenci: cal login interactiu.
        if (err instanceof InteractionRequiredAuthError) {
          this.msalService.loginRedirect({ scopes: environment.apiConfig.scopes });
        }
        return throwError(() => err);
      }),
      finalize(() => (this.exchangeInFlight = undefined))
    );

    return this.exchangeInFlight;
  }

  /**
   * Retorna un JWT valid. Espera a que MSAL finalitzi qualsevol interacció
   * (evita cridar al backend durant un redirect/silent).
   * Si no hi ha JWT o esta expirat, executa la crida al backend del exchange i n'espera el resultat.
   */
  getValidJwt$(): Observable<string> {
    return this.msalBroadcast.inProgress$.pipe(
      // Espera a que no hi hagi cap interacció de MSAL en curs
      filter(status => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        const jwt = this.jwt();
        if (jwt && !this.isExpired(jwt)) return of(jwt);
        return this.exchangeToken();
      })
    );
  }

  logout() {
     const account = this.authMsalService.instance.getActiveAccount()
     this.msalService.logoutRedirect({account});

  }
}
